import React from 'react';
import { Chip, Tooltip, ChipProps } from '@mui/material';
import {
  CheckCircle as ActiveIcon,
  Cancel as CancelIcon,
  HourglassEmpty as UnfilledIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Folder as OnFileIcon,
  Pause as SuspendIcon,
  Help as UnknownIcon,
} from '@mui/icons-material';

interface RxStatusBadgeProps {
  status?: number;
  size?: 'small' | 'medium';
  showIcon?: boolean;
}

interface StatusDisplay {
  label: string;
  color: ChipProps['color'];
  icon: React.ReactElement;
  tooltipText: string;
  isValid: boolean;
}

const VALID_RX_STATUSES = [1, 3, 4, 6, 8]; // Active, Unfilled, Not Dispensed, On File, Suspended
const INVALID_RX_STATUSES = [2, 5, 7]; // Cancelled, Deleted/Void, Unknown

const getStatusDisplay = (code: number): StatusDisplay => {
  switch (code) {
    case 1:
      return {
        label: 'Active',
        color: 'success',
        icon: <ActiveIcon fontSize="inherit" />,
        tooltipText: 'Active - Valid for dispensing',
        isValid: true,
      };
    case 2:
      return {
        label: 'Cancelled',
        color: 'error',
        icon: <CancelIcon fontSize="inherit" />,
        tooltipText: 'Cancelled - INVALID - Do not dispense',
        isValid: false,
      };
    case 3:
      return {
        label: 'Unfilled',
        color: 'warning',
        icon: <UnfilledIcon fontSize="inherit" />,
        tooltipText: 'Unfilled - Valid, waiting to be filled',
        isValid: true,
      };
    case 4:
      return {
        label: 'Not Dispensed',
        color: 'info',
        icon: <InfoIcon fontSize="inherit" />,
        tooltipText: 'Not Dispensed - Valid, filled but not yet dispensed',
        isValid: true,
      };
    case 5:
      return {
        label: 'Deleted/Void',
        color: 'error',
        icon: <DeleteIcon fontSize="inherit" />,
        tooltipText: 'Deleted/Void - INVALID - Do not dispense',
        isValid: false,
      };
    case 6:
      return {
        label: 'On File',
        color: 'success',
        icon: <OnFileIcon fontSize="inherit" />,
        tooltipText: 'On File - Valid for future use',
        isValid: true,
      };
    case 7:
      return {
        label: 'Status 7',
        color: 'error',
        icon: <UnknownIcon fontSize="inherit" />,
        tooltipText: 'Unknown Status 7 - INVALID - Verify before dispensing',
        isValid: false,
      };
    case 8:
      return {
        label: 'Suspended',
        color: 'warning',
        icon: <SuspendIcon fontSize="inherit" />,
        tooltipText: 'Suspended - Valid but temporarily on hold',
        isValid: true,
      };
    default:
      return {
        label: `Status ${code}`,
        color: 'default',
        icon: <UnknownIcon fontSize="inherit" />,
        tooltipText: `Unknown Status Code: ${code} - Verify before dispensing`,
        isValid: false,
      };
  }
};

export const RxStatusBadge: React.FC<RxStatusBadgeProps> = ({
  status,
  size = 'small',
  showIcon = true
}) => {
  // Handle undefined status
  if (status === undefined || status === null) {
    return (
      <Tooltip title="Rx Status: Unknown - No status information available">
        <Chip
          label="Unknown Status"
          size={size}
          color="default"
          variant="outlined"
          icon={showIcon ? <UnknownIcon fontSize="inherit" /> : undefined}
        />
      </Tooltip>
    );
  }

  const display = getStatusDisplay(status);

  return (
    <Tooltip title={display.tooltipText} arrow>
      <Chip
        label={display.label}
        color={display.color}
        size={size}
        variant={display.isValid ? 'outlined' : 'filled'}
        icon={showIcon ? display.icon : undefined}
        sx={{
          fontWeight: display.isValid ? 'normal' : 'bold',
          ...(display.isValid ? {} : {
            boxShadow: 1,
            borderWidth: 2,
          }),
        }}
      />
    </Tooltip>
  );
};

// Helper function to check if a status is valid
export const isValidRxStatus = (status?: number): boolean => {
  if (status === undefined || status === null) return false;
  return VALID_RX_STATUSES.includes(status);
};

// Export constants for use in other components
export { VALID_RX_STATUSES, INVALID_RX_STATUSES };

// ---------------------------------------------------------------------------
// Kroll Rx.AdjState — the fill's adjudication state, a DIFFERENT axis from the
// numeric Rx.Status above. Kroll.Pharmacy.Data.RxAdjState (verified from
// Kroll's own enum dump, 2026-08-28):
//   NotAdjudicated=0, Adjudicating=1, Done=3, NHBatched=4, NeedsReview=5,
//   ThirdPartyDown=6, NoResponse=7
// Kroll's profile shows these fills as "Pending Adj" / "Needs Review" while
// Rx.Status still reads Active — so a status badge alone hides a stuck claim
// (GSP Mississauga Rx 2423205 / 2421644, 2026-08-28). This badge flags every
// non-clean state and renders nothing when adjudication completed (Done /
// NHBatched) or the payload predates the field.
// ---------------------------------------------------------------------------

interface RxAdjStateBadgeProps {
  adjState?: number | null;
  size?: 'small' | 'medium';
}

const CLEAN_ADJ_STATES = [3, 4]; // Done, NHBatched

const getAdjStateDisplay = (code: number): { label: string; tooltipText: string } => {
  switch (code) {
    case 0:
      return {
        label: 'Pending adjudication',
        tooltipText: 'Kroll has not adjudicated this fill yet (Kroll shows "Pending Adj") - the claim was never sent. Complete it in Kroll.',
      };
    case 1:
      return {
        label: 'Adjudicating',
        tooltipText: 'Kroll is mid-adjudication on this fill. If this persists, the claim is stuck - check Kroll.',
      };
    case 5:
      return {
        label: 'Needs review',
        tooltipText: 'Kroll flagged this fill Needs Review - adjudication did not complete cleanly (rejected or unresolved claim). Resolve it in Kroll.',
      };
    case 6:
      return {
        label: 'Third party down',
        tooltipText: 'The insurer host was down when Kroll tried to adjudicate this fill. The claim has not been accepted - retry in Kroll.',
      };
    case 7:
      return {
        label: 'No response',
        tooltipText: 'The insurer never responded to this claim. The claim has not been accepted - retry in Kroll.',
      };
    default:
      return {
        label: `Adj state ${code}`,
        tooltipText: `Unknown Kroll adjudication state ${code} - verify this fill in Kroll before dispensing.`,
      };
  }
};

/**
 * Shared label resolver for the adjudication flag: null = clean (Done /
 * NHBatched) or absent, otherwise the warning label. Custom-styled surfaces
 * (e.g. UnifiedRxNavigator's mini pills) use this so the wording stays in one
 * place; RxAdjStateBadge renders the standard Chip from the same mapping.
 */
export const getRxAdjStateFlagLabel = (adjState?: number | null): string | null => {
  if (adjState === undefined || adjState === null) return null;
  const code = Number(adjState);
  if (CLEAN_ADJ_STATES.includes(code)) return null;
  return getAdjStateDisplay(code).label;
};

export const RxAdjStateBadge: React.FC<RxAdjStateBadgeProps> = ({
  adjState,
  size = 'small',
}) => {
  // Absent = payload predates the field; never invent a state.
  if (adjState === undefined || adjState === null) return null;

  const code = Number(adjState);
  if (CLEAN_ADJ_STATES.includes(code)) return null;

  const display = getAdjStateDisplay(code);

  return (
    <Tooltip title={display.tooltipText} arrow describeChild>
      <Chip
        label={display.label}
        color="warning"
        size={size}
        variant="filled"
        sx={{ fontWeight: 'bold', boxShadow: 1 }}
      />
    </Tooltip>
  );
};
